import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { REPORT_KEYS, isValidReportId } from "../queryKeys";
import {
  executeReportAction,
  exportReport,
  runReportV2,
} from "../../../services/reportService";
import { APPLY_DRAFT, REVERT_DRAFT } from "../../../constants/reportActions";
import { REPORT_MESSAGES } from "../../../utils/reportsMessages";

export function useReportMutations() {
  const queryClient = useQueryClient();

  // Targets only the sidepanel list
  const invalidateMetadata = () =>
    queryClient.invalidateQueries({ queryKey: REPORT_KEYS.metadata });

  // Targets definition, results, and summary for a specific report via hierarchy
  const invalidateReportData = (reportId: string) =>
    queryClient.invalidateQueries({ queryKey: REPORT_KEYS.detail(reportId) });

  const actionMutation = useMutation({
    mutationFn: ({ id, action, ...payload }: { id: string; action: string; [key: string]: any }) => {
      if (!isValidReportId(id)) throw new Error("Invalid report ID provided to action");
      return executeReportAction(id, action, payload);
    },
    onSuccess: (data, { id, action }) => {
      toast.success(REPORT_MESSAGES[action] || "Action successful");
      
      invalidateMetadata();

      if (action === APPLY_DRAFT && data.id !== id) {
        invalidateReportData(data.id);
      } else if (action === REVERT_DRAFT) {
        invalidateReportData(id);
      }
    },
    onError: () => toast.error("Operation failed"),
  });

  const runMutation = useMutation({
    mutationFn: ({
      reportType,
      parameters,
      reportId,
      mode,
    }: {
      reportType: string;
      parameters: Record<string, string>;
      reportId?: string;
      mode: "new" | "draft" | "refresh";
    }) => {
      if (mode !== "new" && !isValidReportId(reportId)) throw new Error("Invalid report ID provided for run");
      return runReportV2({ reportType, parameters, reportId }, mode);
    },
    onSuccess: (data, { reportId }) => {
      invalidateMetadata();
      
      const targetId = reportId || data.reportId;
      if (targetId) {
        invalidateReportData(targetId);
      }
    },
    onError: () => toast.error("Failed to run report"),
  });

  const exportMutation = useMutation({
    mutationFn: (reportId: string) => {
      if (!isValidReportId(reportId)) throw new Error("Invalid report ID provided for export");
      return exportReport({reportId});
    },
    onSuccess: ({ downloadUrl }) => {
      window.open(downloadUrl, "_blank");
    },
    onError: () => toast.error("Export failed"),
  });

  return { actionMutation, runMutation, exportMutation };
}
